import React, { useEffect, useState, useCallback } from "react";
import MaintenanceItemCard from "./MiniComponents/MaintenanceItemCard";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { debounce } from "lodash";
import { getAllStandardMaintenanceItems } from "../../../redux/StandardMaintenanceItemSlice/StandardMaintenanceItemSlice";
import "./MaintenanceItems.css";

export default function MaintenanceItems() {
  const dispatch = useDispatch();
  const { allStandardMaintenanceItems, status } = useSelector(
    (state) => state.standardMaintenanceItems
  );

  // Local State
  const [allItemsData, setAllItemsData] = useState([]);
  const [displayedItems, setDisplayedItems] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [loading, setLoading] = useState(true);

  // جلب كل البيانات عند التحميل
  useEffect(() => {
    const fetchAllItems = async () => {
      try {
        setLoading(true);

        // جلب أول صفحة
        const firstPage = await dispatch(
          getAllStandardMaintenanceItems(1)
        ).unwrap();
        let allData = [...(firstPage.data || [])];

        const totalPages = firstPage.pagination
          ? Math.ceil(firstPage.pagination.total / firstPage.pagination.limit)
          : 1;

        console.log(`📄 Total maintenance items pages: ${totalPages}`);

        // جلب باقي الصفحات
        if (totalPages > 1) {
          const promises = [];
          for (let page = 2; page <= totalPages; page++) {
            promises.push(
              dispatch(getAllStandardMaintenanceItems(page)).unwrap()
            );
          }

          const otherPages = await Promise.all(promises);
          otherPages.forEach((pageData) => {
            if (pageData.data) {
              allData = [...allData, ...pageData.data];
            }
          });
        }

        console.log(`✅ Loaded all ${allData.length} maintenance items`);
        setAllItemsData(allData);
        setDisplayedItems(allData);
      } catch (error) {
        console.error("❌ Error loading maintenance items:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchAllItems();
  }, [dispatch]);

  // البحث المحلي في الـ Frontend
  const searchInFrontend = useCallback((keyword, data) => {
    if (!keyword.trim()) {
      return data;
    }

    const searchTerm = keyword.toLowerCase().trim();

    return data.filter((item) => {
      const name = item.name?.toLowerCase() || "";
      const nameEn = item.name_en?.toLowerCase() || "";
      const description = item.description?.toLowerCase() || "";
      const category = item.category?.toLowerCase() || "";
      const parentName = item.parent?.name?.toLowerCase() || "";
      const parentNameEn = item.parent?.name_en?.toLowerCase() || "";

      return (
        name.includes(searchTerm) ||
        nameEn.includes(searchTerm) ||
        description.includes(searchTerm) ||
        category.includes(searchTerm) ||
        parentName.includes(searchTerm) ||
        parentNameEn.includes(searchTerm)
      );
    });
  }, []);

  // Debounced search function
  const debouncedSearch = useCallback(
    debounce((keyword) => {
      console.log("🔍 Frontend Search for:", keyword);

      if (keyword.trim() !== "") {
        setIsSearching(true);
        const results = searchInFrontend(keyword, allItemsData);
        console.log(`✅ Found ${results.length} results`);
        setDisplayedItems(results);
      } else {
        setIsSearching(false);
        setDisplayedItems(allItemsData);
      }
    }, 300),
    [allItemsData, searchInFrontend]
  );

  // Handle search input change
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    debouncedSearch(value);
  };

  // Clear search
  const clearSearch = () => {
    setSearchQuery("");
    setIsSearching(false);
    setDisplayedItems(allItemsData);
  };

  return (
    <>
      <div className="main">
        <section className="maintenance-items">
          <div>
            <h1 className="text-white">عناصر الصيانة</h1>
          </div>
          <div className="container-fluid">
            <div className="row justify-content-between align-items-center mb-5 mt-3">
              {/* عدد عناصر الصيانة */}
              <div className="col-md-3 d-flex justify-content-md-start justify-content-center mb-3">
                <div className="card py-2 px-3">
                  <p className="mb-0 primary-100">
                    {isSearching ? (
                      <>
                        نتائج البحث:{" "}
                        <span className="primary-100">
                          {displayedItems.length}
                        </span>
                        <small
                          className="text-muted d-block"
                          style={{ fontSize: "0.8rem" }}
                        >
                          (من إجمالي {allItemsData.length})
                        </small>
                      </>
                    ) : (
                      <>
                        عدد عناصر الصيانة:{" "}
                        <span className="primary-100">
                          {allItemsData.length}
                        </span>
                      </>
                    )}
                  </p>
                </div>
              </div>

              {/* Search Box */}
              <div className="col-md-5 text-center mb-3">
                <div className="search-box-maintenance position-relative">
                  <input
                    type="text"
                    placeholder="ابحث بالاسم (عربي/إنجليزي)، الوصف، الفئة..."
                    className="form-control search-input-maintenance"
                    value={searchQuery}
                    onChange={handleSearchChange}
                  />
                  <i className="fas fa-search search-icon-maintenance"></i>
                  {searchQuery && (
                    <button
                      className="btn-clear-search-maintenance"
                      onClick={clearSearch}
                      title="مسح البحث"
                    >
                      <i className="fas fa-times"></i>
                    </button>
                  )}
                </div>
                {isSearching && (
                  <div className="search-info-maintenance text-center mt-2">
                    <small className="text-success">
                      <i className="fas fa-check-circle me-1"></i>
                      وجد {displayedItems.length} نتيجة للبحث عن "{searchQuery}"
                    </small>
                  </div>
                )}
              </div>

              {/* زر إضافة عنصر صيانة */}
              <div className="col-md-3 text-md-end text-center mb-3">
                <Link
                  className="btn btn-primary text-white"
                  to={"/AddMaintenanceItem"}
                >
                  <i className="fas fa-plus me-2"></i>
                  إضافة عنصر صيانة
                </Link>
              </div>
            </div>

            {/* Loading Spinner */}
            {loading ? (
              <div className="loading-spinner-maintenance">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">جاري التحميل...</span>
                </div>
                <p className="text-center mt-3 text-muted">
                  جاري تحميل جميع عناصر الصيانة...
                </p>
              </div>
            ) : (
              <>
                {/* Items Grid */}
                <div className="row">
                  {displayedItems && displayedItems.length > 0 ? (
                    displayedItems.map((item) => (
                      <div
                        key={item.id}
                        className="col-xxl-3 col-xl-4 col-lg-4 col-md-6 col-sm-6 col-12 mb-3"
                      >
                        <MaintenanceItemCard item={item} />
                      </div>
                    ))
                  ) : (
                    <div className="col-12 text-center py-5">
                      <i className="fas fa-tools fa-3x text-muted mb-3"></i>
                      <p className="text-muted">
                        {searchQuery
                          ? `لا توجد نتائج للبحث عن "${searchQuery}"`
                          : "لا توجد عناصر صيانة"}
                      </p>
                      {searchQuery && (
                        <button
                          className="btn btn-sm btn-primary mt-2"
                          onClick={clearSearch}
                        >
                          <i className="fas fa-times me-2"></i>
                          مسح البحث وعرض كل العناصر
                        </button>
                      )}
                    </div>
                  )}
                </div>

                {/* Results Summary */}
                {displayedItems.length > 0 && (
                  <div className="results-summary-maintenance text-center mt-4">
                    <p className="text-muted">
                      عرض {displayedItems.length} عنصر صيانة
                      {isSearching && ` من إجمالي ${allItemsData.length}`}
                    </p>
                  </div>
                )}
              </>
            )}
          </div>
        </section>
      </div>
    </>
  );
}
